// Step 7 Phase 0 — Mobile OOM guardrail (client-side soft warning).
//
// Background: iPhone 13 Pro Max + Chrome iOS killed the tab on
// `model_freeze.ply` 174 MB raw (verified Step 7 QA bug B1, Spike #1).
// WebKit iOS imposes a per-tab memory budget around 1.88 GB JS heap
// (cfr. https://bugs.webkit.org/show_bug.cgi?id=268816). A raw `.ply`
// 3DGS file expands ~3-4× during decode (chunks + Blob + GPU buffer
// + Three.js scene), so 174 MB raw can peak ~600-800 MB and trigger
// OOM kill.
//
// Phase 0 is a guardrail BEFORE Spark+SOG migration (Fase 3+). It
// only targets the format that's known-bad on phones: raw `.ply`.
// Compressed formats (`.compressed.ply`, `.sog`) are not addressed
// here because:
//  - they are not yet valid `ModelMeta.format` values (Fase 2 widens
//    the type system),
//  - they are intrinsically mobile-friendly (4× / 17× compression
//    ratios verified in Spike #1),
//  - the manifest+variant flow in Fase 6 will handle them with the
//    proper adaptive negotiation pattern, not this binary guardrail.
//
// Tablet handling: NOT covered in Phase 0. iPad has higher memory
// budget but can still OOM on multi-hundred-MB raw PLYs. Fase 6
// will introduce splat-count-based budgets per device class. For
// the immediate go-live blocker, only `mobile` (phones) is gated.

import type { ModelMeta } from '../api/types.js';
import type { DeviceClass } from '../lib/device.js';

// 150 MB. Conservative threshold based on Spike #1 RAM peak observation
// (~2× model size during chunk-then-Blob load) and iPhone 13 Pro Max
// crash repro at 174 MB raw PLY. Older iPhones (11/12) likely have
// tighter budgets; this errs on the safe side.
export const MOBILE_PLY_SOFT_LIMIT_BYTES = 150 * 1024 * 1024;

/**
 * Returns true when the meta indicates a load that's likely to OOM
 * the current mobile browser tab. Phase 0 scope: only `format='ply'`
 * on `mobile` device class above the soft limit.
 *
 * Intentionally narrow: returns false for any other combination so
 * future format additions (compressed.ply, sog) don't accidentally
 * trigger this legacy guardrail — Fase 6 has the adaptive flow.
 */
export function shouldWarnMobileSize(
  deviceClass: DeviceClass,
  meta: Pick<ModelMeta, 'format' | 'size_bytes'>,
): boolean {
  if (deviceClass !== 'mobile') return false;
  if (meta.format !== 'ply') return false;
  return meta.size_bytes > MOBILE_PLY_SOFT_LIMIT_BYTES;
}
